import { mqttStore } from "@/store/mqtt"
import { useUserStore } from "@/store/userStore"
import * as Sentry from '@sentry/vue'
import { safeStringify } from '@/utils/safeStringify'
import { redactText, sanitizeLogArgs } from '@/utils/logSanitizer'

type LogLevel = "debug" | "info" | "warn" | "error"

const isDev = import.meta.env.DEV
const enableProdConsole = import.meta.env.VITE_ENABLE_PROD_CONSOLE === 'true'
const mqttTopic = "frontend/logging"
let isLogging = false

const logToConsole = (level: LogLevel, args: any[]) => {
  const timestamp = new Date().toISOString()
  const prefix = `${timestamp} [${level.toUpperCase()}]:`

  const methods: Record<LogLevel, (...args: any[]) => void> = {
    error: console.error,
    warn: console.warn,
    info: console.info,
    debug: console.debug || console.log,
  }

  methods[level](prefix, ...args)
}

const logToMqtt = async (level: LogLevel, args: any[]) => {
  const { client, publishMessage } = mqttStore()
  const { userName } = useUserStore()
  if (!userName) return

  // IFB-163: args are already sanitised; the final regex pass is a backstop
  // for any token-shaped string that survived serialisation.
  const payload = redactText(safeStringify({
    type: level === 'warn' ? 'warning' : level,
    args,
    timestamp: new Date().toISOString(),
    user_name: userName,
    source: 'idi_v3_dashboard'
  }))

  try {
    if (client) {
      client.publish(mqttTopic, payload, { qos: 1 })
    } else {
      if (isLogging) return // Prevent recursive logging loop
      isLogging = true
      await publishMessage(mqttTopic, payload, 1)
      isLogging = false
    }
  } catch (error) {
    console.error('Failed to publish log:', error)
    isLogging = false
  }
}

const addSentryBreadcrumb = (level: LogLevel, args: any[]) => {
  const sentryLevel = level === 'debug' ? 'debug' : level === 'info' ? 'info' : level === 'warn' ? 'warning' : 'error'

  Sentry.addBreadcrumb({
    category: 'log',
    message: redactText(args.map(arg => typeof arg === 'object' ? safeStringify(arg) : String(arg)).join(' ')),
    level: sentryLevel,
    timestamp: Date.now() / 1000,
  })
}

const logMessage = (level: LogLevel, ...args: any[]) => {
  // Always log to console in dev, or in prod if enabled
  if (isDev || enableProdConsole) {
    logToConsole(level, args)
  }

  // Remote sinks never see raw Axios errors (they carry the Authorization
  // header) or token-shaped strings. The console above keeps the full object.
  const remoteArgs = sanitizeLogArgs(args)

  // In production, also send to MQTT
  if (!isDev) {
    logToMqtt(level, remoteArgs)
  }

  // Add breadcrumb to Sentry for warn and error levels
  if (level === 'warn' || level === 'error') {
    addSentryBreadcrumb(level, remoteArgs)
  }
}

const log = {
  debug: (...args: any[]) => logMessage("debug", ...args),
  info: (...args: any[]) => logMessage("info", ...args),
  warn: (...args: any[]) => logMessage("warn", ...args),
  error: (...args: any[]) => logMessage("error", ...args),
}

export default log
